import { TableModel, TableStateUrl, FiltersHandler } from '@/core/table';

export default class ListPageModel {
    /**
     *
     * @param {RestResource} api
     * @param {Object} state
     * @param {VueRouter} state.router
     * @param {Object} state.serializers
     * @param {Object} tableDefinition
     * @param {string} [tableDefinition.uniqueField]
     * @param {Object} [tableDefinition.filters]
     * @param {Object} tableDefinition.columns
     * @param {Object} tableDefinition.actions
     * @param {Object} [tableDefinition.dependencies]
     * @param {boolean} [tableDefinition.hasMobileView]
     * @param {Boolean} [tableDefinition.fixedHeader=true]
     * @param {Boolean} [tableDefinition.actionsAlwaysVisible=false]
     * @param {Function} [tableDefinition.rowClass]
     * @param {TableModelConfig} config
     */
    constructor(api, state, tableDefinition, config = {}) {
        const {
            uniqueField, filters, columns, actions, dependencies, rowClass, actionsWidth,
            hasMobileView, onApiLoaded = () => {}, onFilterChange = () => {},
        } = tableDefinition;

        const onLoaded = (loadData) => {
            onApiLoaded(loadData, this);
        };

        const table = new TableModel(api, config, onLoaded, onFilterChange);
        const filtersProvider = new FiltersHandler(filters || {});
        const tableState = new TableStateUrl(state.router, state.serializers);
        table.register([
            tableState,
            filtersProvider,
        ]);

        this.config = config;
        this.table = table;
        this.actionsWidth = actionsWidth;
        this.uniqueField = uniqueField;
        this.columns = columns;
        this.actions = actions;
        this.filters = filters;
        this.hasMobileView = hasMobileView;
        this.isMobileView = false;
        this.fixedHeader = tableDefinition.fixedHeader === undefined ? true : tableDefinition.fixedHeader;
        this.actionsAlwaysVisible = tableDefinition.actionsAlwaysVisible;
        this.dependencies = dependencies;
        this.rowClass = rowClass;
        this.filtersProvider = filtersProvider;
        this.tableState = tableState;
        this.stateVersion = 0;
        this.api = api;
        this.routeHandler = state.router.beforeEach((to, from, next) => this.handleRouteChange(to, from, next));
    }

    updateMobileView(mobileView) {
        this.isMobileView = mobileView;
    }

    restore() {
        return this.table.restore(!this.isMobileView);
    }

    refresh() {
        return this.table.load();
    }

    removeRow(row) {
        return this.table.removeRow(row);
    }

    toggleInfinityMode(state) {
        this.tableState.trackPage = !state;
        this.isMobileView = !!state;
    }

    handleRouteChange(to, from, next) {
        if (to && from.name !== to.name) {
            next();
            this.routeHandler();
            return;
        }
        const state = this.tableState.update(to, from);
        if (this.filters !== undefined) {
            const filters = state.filters;
            Object.values(this.filters).forEach(({ field, filter }) => {
                filter.load(filters[field], filters);
            });
        }
        if (!this.isMobileView) {
            this.refresh();
        }
        next();
    }

    async fetch(page) {
        await this.table.fetch(page);
        return this.table.items;
    }

    paging({ currentPage, itemsPerPage }) {
        this.stateVersion += 1;
        return this.table.paging(currentPage, itemsPerPage);
    }

    filter(field, value) {
        this.stateVersion += 1;
        if (this.isReset(value)) {
            this.table.reset();
            return;
        }
        if (typeof field !== 'string') {
            this.table.filter(field);
            return;
        }
        if (!this.table.isSameFilter(field, value)) {
            this.table.filter({ [field]: value });
        }
    }

    updateFilters(field, value) {
        this.stateVersion += 1;
        if (this.isReset(value)) {
            this.table.reset();
            return;
        }
        if (typeof field !== 'string') {
            this.table.filter(field);
            return;
        }
        if (!this.table.isSameFilter(field, value)) {
            this.table.updateFilters({ [field]: value });
        }
    }

    sort(column, isSortAsc) {
        this.stateVersion += 1;
        this.table.sort(column.sortField || column.field, column.dir || (isSortAsc ? 'asc' : 'desc'));
    }

    hasPages() {
        return this.table.hasPages();
    }

    hasItems() {
        return this.table.hasItems();
    }

    hasFailedRequests() {
        return this.table.failed;
    }

    getError() {
        return this.table.error;
    }

    isReset(value) {
        return this.config.resetValue && this.config.resetValue === value;
    }
}
